/**
 * Dashboard display constants.
 *
 * Keeps all presentational metadata (icons, labels, colours) out of
 * components so they stay lean and the mapping lives in one place.
 */
import {
  FileText,
  Calculator,
  TrendingUp,
  Briefcase,
  Users,
  Home,
  Building2,
  CreditCard,
  Receipt,
  BookOpen,
  HelpCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { CustomerServiceRequestWorkflowState, CustomerServiceRequestResponse } from '@/api/service-requests';

// ── Service metadata ──────────────────────────────────────────────────────────

export interface ServiceMeta {
  title: string;
  description: string;
  Icon: LucideIcon;
}

/** Maps API `service_type` (snake_case) → display metadata. */
export const SERVICE_META: Record<string, ServiceMeta> = {
  itr_filing: {
    title: 'ITR Filing',
    description:
      'End-to-end income tax return filing with AI optimisation and expert CA review.',
    Icon: FileText,
  },
  advance_tax_planning: {
    title: 'Advance Tax Planning',
    description:
      'Calculate and pay advance tax instalments accurately to avoid interest penalties.',
    Icon: Calculator,
  },
  ltcg_harvesting: {
    title: 'LTCG ₹1.25L Harvesting',
    description:
      'Optimise long-term capital gains up to the annual tax-free threshold.',
    Icon: TrendingUp,
  },
  huf_registration: {
    title: 'HUF Registration',
    description:
      'Create a Hindu Undivided Family entity for significant tax savings.',
    Icon: Home,
  },
  huf_planning: {
    title: 'HUF Planning',
    description: 'Strategic tax planning and management using your HUF entity.',
    Icon: Home,
  },
  gst_registration: {
    title: 'GST Registration',
    description:
      'Get registered for Goods and Services Tax smoothly and compliantly.',
    Icon: Briefcase,
  },
  gstr_bookkeeping: {
    title: 'GSTR / Book-Keeping',
    description:
      'Monthly GST return filing and professional bookkeeping, end-to-end.',
    Icon: Receipt,
  },
  accounting_bookkeeping: {
    title: 'Accounting & Bookkeeping',
    description:
      'Accurate financial records and transaction management for your business.',
    Icon: Users,
  },
  business_registration: {
    title: 'Business Registration',
    description:
      'Register your company or LLP legally and without the usual hassle.',
    Icon: Building2,
  },
  gst_return_filing: {
    title: 'GST Return Filing',
    description: 'File GST returns on time, with zero errors and expert support.',
    Icon: FileText,
  },
  pan_tan_application: {
    title: 'PAN / TAN Application',
    description: 'Apply for a PAN or TAN number quickly and securely.',
    Icon: CreditCard,
  },
  tds_return_filing: {
    title: 'TDS Return Filing',
    description:
      'Manage and file TDS returns accurately and on schedule every quarter.',
    Icon: BookOpen,
  },
  other: {
    title: 'Custom Request',
    description:
      'Describe what you need — we\'ll match you with the right expert.',
    Icon: HelpCircle,
  },
};

/** Fallback for service types not listed above. */
export function getServiceMeta(serviceType: string): ServiceMeta {
  if (SERVICE_META[serviceType]) return SERVICE_META[serviceType];
  return {
    title: serviceType
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' '),
    description: 'Professional financial service assistance.',
    Icon: FileText,
  };
}

// ── Featured services (curated dashboard grid) ────────────────────────────────
//
// These 6 cards are the canonical "Available services" line-up shown on the
// dashboard. Pricing and TAT are baked in here (not driven by the API) so we
// can ship a tight, on-brand grid even before the catalogue endpoint is fully
// stable. The `service_type` value still drives `/service-request/new/...`
// navigation and the actual server-side create call.

export interface FeaturedService {
  /** snake_case API service_type — drives `Start Now` navigation. */
  service_type: string;
  title:        string;
  description:  string;
  /** Display string, e.g. "₹795 onwards". Bypass API pricing entirely. */
  priceLabel:   string;
  /** Display string, e.g. "2–7 days". Bypass API expected_tat entirely. */
  tatLabel:     string;
  Icon:         LucideIcon;
}

export const FEATURED_SERVICES: FeaturedService[] = [
  {
    service_type: 'itr_filing',
    title:        'ITR Filing',
    description:  'File your taxes with optimisation & accuracy',
    priceLabel:   '₹795 onwards',
    tatLabel:     '2–7 days',
    Icon:         FileText,
  },
  {
    service_type: 'advance_tax_planning',
    title:        'Advance Tax Planning',
    description:  'Avoid interest & plan your taxes in advance',
    priceLabel:   '₹595 onwards',
    tatLabel:     '2–5 days',
    Icon:         Calculator,
  },
  {
    service_type: 'gst_registration',
    title:        'GST Registration',
    description:  'Get GST registered with complete setup support',
    priceLabel:   '₹595 onwards',
    tatLabel:     '1–4 days',
    Icon:         Briefcase,
  },
  {
    service_type: 'accounting_bookkeeping',
    title:        'Accounting & Bookkeeping',
    description:  'Maintain clean books & stay compliance ready',
    priceLabel:   '₹995 onwards',
    tatLabel:     '5–8 days',
    Icon:         Users,
  },
  {
    service_type: 'huf_registration',
    title:        'HUF Registration & Planning',
    description:  'Build family tax structure & optimise savings',
    priceLabel:   '₹2,995 onwards',
    tatLabel:     '7–10 days',
    Icon:         Home,
  },
  {
    service_type: 'tds_return_filing',
    title:        'TDS Filing',
    description:  'File TDS returns with accurate reconciliation',
    priceLabel:   '₹895 onwards',
    tatLabel:     '2–4 days',
    Icon:         BookOpen,
  },
];

// ── Workflow state metadata ───────────────────────────────────────────────────

export type WorkflowVariant = 'amber' | 'blue' | 'green' | 'muted';

export interface WorkflowMeta {
  label: string;
  progress: number;
  variant: WorkflowVariant;
}

/** Maps `customer_workflow_state` → display metadata. */
export const WORKFLOW_STATE_META: Record<
  CustomerServiceRequestWorkflowState | string,
  WorkflowMeta
> = {
  service_selected:             { label: 'Service Selected',    progress: 12,  variant: 'amber' },
  docs_start:                   { label: 'Documents Requested', progress: 30,  variant: 'amber' },
  provider_assignment_start:    { label: 'Finding Provider',    progress: 48,  variant: 'blue'  },
  provider_assignment_complete: { label: 'Provider Assigned',   progress: 62,  variant: 'blue'  },
  platform_clarification:       { label: 'Clarification',       progress: 74,  variant: 'amber' },
  draft_evaluation:             { label: 'Draft Review',        progress: 86,  variant: 'blue'  },
  acknowledgement_submission:   { label: 'Filing',              progress: 94,  variant: 'blue'  },
  service_request_complete:     { label: 'Completed',           progress: 100, variant: 'green' },
};

export function getWorkflowMeta(state: string | undefined | null): WorkflowMeta {
  if (!state) return { label: 'In Progress', progress: 10, variant: 'muted' };
  return WORKFLOW_STATE_META[state] ?? { label: state.replace(/_/g, ' '), progress: 10, variant: 'muted' };
}

// ── Category display labels ───────────────────────────────────────────────────

export const CATEGORY_DISPLAY: Record<string, string> = {
  itr:        'ITR',
  gst:        'GST',
  huf:        'HUF',
  individual: 'Individual',
  business:   'Business',
  tax:        'Tax',
  audit:      'Audit',
  compliance: 'Compliance',
};

export function getCategoryLabel(cat: string): string {
  return CATEGORY_DISPLAY[cat] ?? cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Service request status helpers ───────────────────────────────────────────

export function isCompleted(sr: CustomerServiceRequestResponse): boolean {
  return (
    sr.service_state === 'COMPLETED' ||
    sr.customer_workflow_state === 'service_request_complete'
  );
}

/** Service Closure approved / performed by admin — a terminal state. */
export function isCancelled(sr: CustomerServiceRequestResponse): boolean {
  return sr.service_state === 'CANCELLED';
}

/** A customer closure request is awaiting admin review. */
export function isOnHold(sr: CustomerServiceRequestResponse): boolean {
  return sr.service_state === 'ON_HOLD';
}

export function isOngoing(sr: CustomerServiceRequestResponse): boolean {
  return !isCompleted(sr) && !isCancelled(sr);
}

/** Terminal requests: completed OR closed. Both live under the "Completed" tab
 *  so a closed service never silently disappears from the dashboard. */
export function isFinished(sr: CustomerServiceRequestResponse): boolean {
  return isCompleted(sr) || isCancelled(sr);
}

/**
 * Closure-aware status pill. `service_state` (ON_HOLD / CANCELLED) overrides
 * the workflow label — the workflow stage is frozen while the service is on
 * hold and meaningless once it is closed.
 */
export function getRequestStatusMeta(sr: CustomerServiceRequestResponse): WorkflowMeta {
  const workflow = getWorkflowMeta(sr.customer_workflow_state);
  if (isCancelled(sr)) return { label: 'Closed',  progress: workflow.progress, variant: 'muted' };
  if (isOnHold(sr))    return { label: 'On hold', progress: workflow.progress, variant: 'amber' };
  return workflow;
}
